<template>
  <div class="btn-group paid-session-actions">
    <button
      data-toggle="dropdown"
      aria-expanded="false"
      class="btn btn-default btn-compact dropdown-toggle"
    >
      More...
    </button>
    <ul class="dropdown-menu dropdown-menu-right pull-right">
      <li>
        <a
          class="flex align-items-center gap-x-xs"
          style="display: flex"
          @click.prevent="openPauseModal"
        >
          <i aria-hidden="true" class="fa-solid fa-pause"></i>
          Pause all payments...
        </a>
      </li>
      <li>
        <a
          class="flex align-items-center gap-x-xs"
          style="display: flex"
          @click.prevent="openResumeModal"
        >
          <i aria-hidden="true" class="fa-solid fa-play"></i>
          Resume all payments...
        </a>
      </li>
      <li>
        <a
          class="flex align-items-center gap-x-xs"
          style="display: flex"
          @click.prevent="openCancelScheduledModal"
        >
          <i aria-hidden="true" class="fa-solid fa-xmark"></i>
          Cancel scheduled pauses...
        </a>
      </li>
    </ul>
    <div
      v-if="showPauseModal"
      class="modal fade in"
      style="display: block"
      @click.self="closePauseModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" @click="closePauseModal">
              &times;
            </button>
            <h4 class="modal-title">Pause payment collection for all subscribers</h4>
          </div>
          <div class="modal-body">
            <p>
              This pauses payment collection on every active subscription in
              {{ sessionName }}. Subscriptions that are already paused are left alone.
              Each family is emailed when their pause takes effect, and paused
              contract-length subscriptions get credit for the paused time when
              they resume.
            </p>
            <p class="font-semi m-t-sm">When should the pause start?</p>
            <div>
              <label class="font-normal">
                <input
                  type="radio"
                  value="now"
                  id="mass-pause-now"
                  v-model="pauseStart"
                />
                Immediately
              </label>
            </div>
            <div>
              <label class="font-normal">
                <input
                  type="radio"
                  value="future"
                  id="mass-pause-future"
                  v-model="pauseStart"
                />
                On a future date
              </label>
            </div>
            <div v-if="pauseStart === 'future'" class="form-group">
              <label class="font-normal">Pause payment collection on</label>
              <div>
                <date-picker
                  v-model="pauseAt"
                  :date="pauseAt"
                  :disabled-date="tomorrowOrLater"
                ></date-picker>
              </div>
            </div>
            <p class="font-semi m-t-sm">
              How long would you like to pause payment collection for?
            </p>
            <div>
              <label class="font-normal">
                <input
                  type="radio"
                  value="indefinite"
                  id="mass-indefinite-pause"
                  v-model="pauseFor"
                />
                Indefinite
              </label>
            </div>
            <div>
              <label class="font-normal">
                <input
                  type="radio"
                  value="specific"
                  id="mass-specific-pause"
                  v-model="pauseFor"
                />
                Until a specific date
              </label>
            </div>
            <div v-if="pauseFor === 'specific'" class="form-group">
              <label class="font-normal">Resume payment collection on</label>
              <div>
                <date-picker
                  v-model="resumeAt"
                  :date="resumeAt"
                  :disabled-date="tomorrowOrLater"
                ></date-picker>
              </div>
            </div>
            <p class="text-danger m-t-xs" v-html="errorMessage" v-if="error"></p>
          </div>
          <div class="modal-footer">
            <button
              class="btn btn-default"
              :disabled="pausing"
              @click="closePauseModal"
            >
              Cancel
            </button>
            <ladda-button
              @lbClicked="pauseAll"
              el-class="btn-warning btn"
              :loading="pausing"
            >
              Pause all payments
            </ladda-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showPauseModal" class="modal-backdrop fade in"></div>
    <div
      v-if="showResumeModal"
      class="modal fade in"
      style="display: block"
      @click.self="closeResumeModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" @click="closeResumeModal">
              &times;
            </button>
            <h4 class="modal-title">Resume payment collection for all subscribers</h4>
          </div>
          <div class="modal-body">
            <p>
              This resumes payment collection on every paused subscription in
              {{ sessionName }}. Each family is emailed when their payments
              resume, and contract-length subscriptions have their paused time
              added back to their expiration date.
            </p>
            <p>
              Subscriptions with a scheduled future pause are not affected.
            </p>
            <p class="text-danger m-t-xs" v-html="errorMessage" v-if="error"></p>
          </div>
          <div class="modal-footer">
            <button
              class="btn btn-default"
              :disabled="resuming"
              @click="closeResumeModal"
            >
              Cancel
            </button>
            <ladda-button
              @lbClicked="resumeAll"
              el-class="btn-primary btn"
              :loading="resuming"
            >
              Resume all payments
            </ladda-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showResumeModal" class="modal-backdrop fade in"></div>
    <div
      v-if="showCancelScheduledModal"
      class="modal fade in"
      style="display: block"
      @click.self="closeCancelScheduledModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" @click="closeCancelScheduledModal">
              &times;
            </button>
            <h4 class="modal-title">Cancel scheduled pauses for all subscribers</h4>
          </div>
          <div class="modal-body">
            <p>
              This cancels the upcoming payment pause on every subscription in
              {{ sessionName }} that has one scheduled. Payment collection
              continues uninterrupted on the normal billing schedule.
            </p>
            <p>
              Subscriptions that are already paused are not affected — use
              resume all payments for those.
            </p>
            <p class="text-danger m-t-xs" v-html="errorMessage" v-if="error"></p>
          </div>
          <div class="modal-footer">
            <button
              class="btn btn-default"
              :disabled="cancelingScheduled"
              @click="closeCancelScheduledModal"
            >
              Go back
            </button>
            <ladda-button
              @lbClicked="cancelScheduledPauses"
              el-class="btn-primary btn"
              :loading="cancelingScheduled"
            >
              Cancel scheduled pauses
            </ladda-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showCancelScheduledModal" class="modal-backdrop fade in"></div>
  </div>
</template>

<script>
import DatePicker from './date_picker.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import { errorableMix } from './mixins/errorable_mix';

export default {
  name: 'paid-session-actions',
  components: {
    DatePicker,
    LaddaButton,
  },
  mixins: [errorableMix],
  props: {
    paidSessionId: {
      type: Number,
      required: true,
    },
    sessionName: {
      type: String,
      default: 'this program',
    },
  },
  data() {
    return {
      showPauseModal: false,
      pausing: false,
      pauseStart: 'now',
      pauseAt: null,
      pauseFor: 'indefinite',
      resumeAt: null,
      showResumeModal: false,
      resuming: false,
      showCancelScheduledModal: false,
      cancelingScheduled: false,
    };
  },
  methods: {
    openPauseModal() {
      this.error = false;
      this.showPauseModal = true;
    },
    closePauseModal() {
      if (this.pausing) return;
      this.showPauseModal = false;
    },
    openResumeModal() {
      this.error = false;
      this.showResumeModal = true;
    },
    closeResumeModal() {
      if (this.resuming) return;
      this.showResumeModal = false;
    },
    openCancelScheduledModal() {
      this.error = false;
      this.showCancelScheduledModal = true;
    },
    closeCancelScheduledModal() {
      if (this.cancelingScheduled) return;
      this.showCancelScheduledModal = false;
    },
    cancelScheduledPauses: _.throttle(function () {
      const vm = this;
      vm.error = false;
      vm.cancelingScheduled = true;
      const url = vm.$apiService.paidSessionCancelScheduledPausesUrl(
        vm.paidSessionId,
      );
      axios
        .post(url)
        .then((response) => {
          vm.$notificationManager.emit(
            'show-toast',
            'Canceling scheduled pauses, this page will refresh when it finishes',
            true,
          );
          vm.cancelingScheduled = false;
          vm.showCancelScheduledModal = false;
          const batchId = response.data ? response.data.batch_id : null;
          if (batchId) {
            vm.pollBatchStatus(batchId);
          } else {
            window.location.reload();
          }
        })
        .catch((error) => {
          vm.errorMessage = this.parseErrorResponse(error);
          vm.error = true;
          vm.cancelingScheduled = false;
        });
    }, 500),
    resumeAll: _.throttle(function () {
      const vm = this;
      vm.error = false;
      vm.resuming = true;
      const url = vm.$apiService.paidSessionResumeSubscriptionsUrl(
        vm.paidSessionId,
      );
      axios
        .post(url)
        .then((response) => {
          vm.$notificationManager.emit(
            'show-toast',
            'Resuming all paused subscriptions, this page will refresh when it finishes',
            true,
          );
          vm.resuming = false;
          vm.showResumeModal = false;
          const batchId = response.data ? response.data.batch_id : null;
          if (batchId) {
            vm.pollBatchStatus(batchId);
          } else {
            window.location.reload();
          }
        })
        .catch((error) => {
          vm.errorMessage = this.parseErrorResponse(error);
          vm.error = true;
          vm.resuming = false;
        });
    }, 500),
    // Func to disable certain dates in the date-picker
    tomorrowOrLater(date) {
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return date < tomorrow;
    },
    pauseAll: _.throttle(function () {
      const vm = this;
      vm.error = false;
      if (vm.pauseStart === 'future' && !vm.pauseAt) {
        vm.errorMessage = 'Please select a date for the pause to start, or switch to pausing immediately.';
        vm.error = true;
        return;
      }
      if (vm.pauseFor === 'specific' && !vm.resumeAt) {
        vm.errorMessage = 'Please select a date to resume payment collection, or switch to an indefinite pause.';
        vm.error = true;
        return;
      }
      if (
        vm.pauseStart === 'future'
        && vm.pauseFor === 'specific'
        && vm.resumeAt <= vm.pauseAt
      ) {
        vm.errorMessage = 'The resume date needs to be after the date the pause starts.';
        vm.error = true;
        return;
      }

      vm.pausing = true;
      const url = vm.$apiService.paidSessionPauseSubscriptionsUrl(
        vm.paidSessionId,
      );
      const params = {
        paid_session: {
          pause_collection: {
            pauses_at:
              vm.pauseStart === 'future' && vm.pauseAt
                ? vm.pauseAt.toISOString()
                : null,
            resumes_at:
              vm.pauseFor === 'specific' && vm.resumeAt
                ? vm.resumeAt.toISOString()
                : null,
          },
        },
      };
      axios
        .post(url, params)
        .then((response) => {
          vm.$notificationManager.emit(
            'show-toast',
            vm.pauseStart === 'future'
              ? 'Scheduling a pause for all active subscriptions, this page will refresh when it finishes'
              : 'Pausing all active subscriptions, this page will refresh when it finishes',
            true,
          );
          vm.pausing = false;
          vm.showPauseModal = false;
          const batchId = response.data ? response.data.batch_id : null;
          if (batchId) {
            vm.pollBatchStatus(batchId);
          } else {
            window.location.reload();
          }
        })
        .catch((error) => {
          vm.errorMessage = this.parseErrorResponse(error);
          vm.error = true;
          vm.pausing = false;
        });
    }, 500),
    pollBatchStatus(batchId) {
      const pollInterval = 2000;
      const batchStatusUrl = `/batch_status/${batchId}.json`;

      const checkStatus = () => {
        axios
          .get(batchStatusUrl)
          .then((response) => {
            if (response.data.is_complete) {
              window.location.reload();
            } else {
              setTimeout(checkStatus, pollInterval);
            }
          })
          .catch(() => {
            // If the status check fails, refresh anyway rather than leaving the page stale.
            window.location.reload();
          });
      };

      setTimeout(checkStatus, pollInterval);
    },
  },
};
</script>
